<script lang="ts" setup>
import { faArrowLeft, faArrowRight, type IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import GButton from '@consumer/components/GButton.vue'
import GIconButton from '@consumer/components/GIconButton.vue'

withDefaults(defineProps<{
  nextLabel: string
  canGoBack?: boolean
  backIcon?: IconDefinition
  backTooltip?: string
}>(), { canGoBack: true,backIcon: faArrowLeft, })

const emit = defineEmits<{
  next: []
  back: []
}>()

// Keyboard navigation. Arrow keys mirror the on-screen next/back
// buttons so desktop users can pan through the reading stage without
// reaching for the mouse. Bound at the document level so focus doesn't
// have to live on the nav bar (the page itself isn't focusable). Other
// shortcut consumers on the page (text inputs, etc.) keep working
// because we ignore the keydown when the target is an editable
// element. The `back` emit is gated on `canGoBack` to match the button
// visibility.
function onKeyDown (e: KeyboardEvent) {
  const target = e.target as HTMLElement | null
  if (target?.isContentEditable) return
  if (
    target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target instanceof HTMLSelectElement
  ) return

  if (e.key === 'ArrowRight') {
    e.preventDefault()
    emit('next')
  }
  else if (e.key === 'ArrowLeft' && __props.canGoBack) {
    e.preventDefault()
    emit('back')
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown)
})
</script>

<template>
  <!--
    @pointerdown.stop prevents button taps from starting a drag on the
    parent ReadingStage (which would otherwise set isSnapping=true and
    swallow the subsequent click).
  -->
  <div class="nav-bar" @pointerdown.stop>
    <Transition name="fade-in-out" :duration="{ enter: 300, leave: 0 }">
      <GIconButton
        v-if="canGoBack"
        v-tooltip="backTooltip"
        :icon="backIcon"
        secondary
        size="large"
        aria-label="Previous page"
        class="glass-button pointer-events-auto"
        @click="$emit('back')"
      />
    </Transition>
    <div class="flex-grow lg:max-w-xs" :class="{'lg:hidden' : !canGoBack}"/>
    <!-- An empty nextLabel hides the button entirely (e.g. the demo's
         transient "gift is opening" beat, which auto-advances). -->
    <GButton
      v-if="nextLabel"
      pill
      secondary
      :label="nextLabel"
      :iconRight="faArrowRight"
      size="small"
      class="glass-button pointer-events-auto w-52 !justify-between"
      @click="$emit('next')"
    />
  </div>
</template>

<style scoped lang="scss">
// Nav bar at the bottom of the reading stage. Sits above the pages so it's
// always tappable. The back button is conditionally rendered (`v-if`) with
// a fade-in transition; the flex spacer next to it keeps the next button
// from jumping horizontally when the back button comes/goes.
.nav-bar {
  align-items: center;
  bottom: var(--nav-bar-bottom);
  display: flex;
  gap: 12px;
  justify-content: space-around;
  left: 0;
  margin: 0 auto;
  max-width: var(--nav-bar-max-width);
  padding: 0 20px;
  pointer-events: none;
  position: absolute;
  right: 0;
  z-index: 20;
}

.glass-button {
  backdrop-filter: blur(7.5px);

  // Translucent glass pane behind the white label/icon.
  background: rgb(70 70 70 / 60%);
  border: none;
  border-radius: 40px;
  box-shadow: 0 4px 13.9px 0 rgb(0 0 0 / 10%);
  color: white;

  &:hover,
  &:focus {
    background: rgb(70 70 70 / 72%);
    color: white !important; // Do not change color
  }
}
</style>
